import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { PermissionEffectToggle } from '../../components/ui/PermissionEffectToggle';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  getPermissionGroup,
  getPermissionGroupGrants,
  listPermissionGroupModuleCatalog,
  PermissionGroupGrant,
  replacePermissionGroupGrants,
  SystemModuleSummary,
} from '../../services/permissionGroupsApi';
import { resolveAdminModuleLabel } from '../../utils/resolveAdminModuleLabel';

type GrantRow = PermissionGroupGrant & { moduleName: string; pillar: string };

const AUTOSAVE_TOAST_ID = 'permission-group-autosave';

function buildGrantMap(modules: SystemModuleSummary[], grants: PermissionGroupGrant[]): GrantRow[] {
  const byModule = new Map(grants.map((grant) => [grant.moduleCode, grant]));

  return modules.map((module) => {
    const existing = byModule.get(module.code);
    return {
      moduleCode: module.code,
      moduleName: resolveAdminModuleLabel(module.name, module.pillar),
      pillar: module.pillar,
      accessEffect: existing?.accessEffect ?? 'none',
      readEffect: existing?.readEffect ?? 'none',
      writeEffect: existing?.writeEffect ?? 'none',
      deleteEffect: existing?.deleteEffect ?? 'none',
    };
  });
}

function toPersistableGrants(rows: GrantRow[]): PermissionGroupGrant[] {
  return rows
    .filter(
      (row) =>
        row.accessEffect !== 'none' ||
        row.readEffect !== 'none' ||
        row.writeEffect !== 'none' ||
        row.deleteEffect !== 'none',
    )
    .map((row) => ({
      moduleCode: row.moduleCode,
      accessEffect: row.accessEffect,
      readEffect: row.readEffect,
      writeEffect: row.writeEffect,
      deleteEffect: row.deleteEffect,
    }));
}

export function PermissionGroupEditorPage() {
  const { groupId = '' } = useParams();
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('access-control.permission-groups');

  const [groupName, setGroupName] = useState('');
  const [rows, setRows] = useState<GrantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveSequenceRef = useRef(0);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const [group, grants, modules] = await Promise.all([
          getPermissionGroup(groupId),
          getPermissionGroupGrants(groupId),
          listPermissionGroupModuleCatalog(),
        ]);
        setGroupName(group.name);
        setRows(buildGrantMap(modules, grants));
      } catch {
        toast.error('Falha ao carregar matriz de permissões.');
      } finally {
        setLoading(false);
      }
    })();
  }, [groupId]);

  const activeRuleCount = useMemo(
    () =>
      rows.filter(
        (row) =>
          row.accessEffect !== 'none' ||
          row.readEffect !== 'none' ||
          row.writeEffect !== 'none' ||
          row.deleteEffect !== 'none',
      ).length,
    [rows],
  );

  async function persistGrants(nextRows: GrantRow[]): Promise<void> {
    if (!canWrite) return;

    const sequence = ++saveSequenceRef.current;
    setSaving(true);
    try {
      await replacePermissionGroupGrants(groupId, toPersistableGrants(nextRows));
      if (sequence !== saveSequenceRef.current) return;
      toast.success('Permissões salvas.', { toastId: AUTOSAVE_TOAST_ID });
    } catch {
      if (sequence !== saveSequenceRef.current) return;
      toast.error('Falha ao salvar permissões.');
    } finally {
      if (sequence === saveSequenceRef.current) {
        setSaving(false);
      }
    }
  }

  function updateRow(
    moduleCode: string,
    patch: Partial<Pick<GrantRow, 'accessEffect' | 'readEffect' | 'writeEffect' | 'deleteEffect'>>,
  ): void {
    if (!canWrite) return;

    const nextRows = rows.map((row) => (row.moduleCode === moduleCode ? { ...row, ...patch } : row));
    setRows(nextRows);
    void persistGrants(nextRows);
  }

  if (loading) {
    return (
      <PageShell>
        <LoadingSpinner />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div>
        <Link to="/admin/permission-groups" className="erp-link-back">
          ← Voltar para grupos
        </Link>
        <PageHeader
          title={`Matriz — ${groupName}`}
          description={
            saving
              ? `Salvando alterações... ${activeRuleCount} módulo(s) com regra configurada.`
              : `${activeRuleCount} módulo(s) com regra configurada. Acesso controla o menu; Leitura controla consultas de dados. Negar prevalece na união dos grupos do colaborador.`
          }
        />
      </div>

      <div className="erp-table-wrap">
        <table className="erp-table">
          <thead>
            <tr>
              <th>Módulo</th>
              <th className="text-center">Acesso</th>
              <th className="text-center">Leitura</th>
              <th className="text-center">Escrita</th>
              <th className="text-center">Exclusão</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.moduleCode}>
                <td>
                  <div className="font-medium">{row.moduleName}</div>
                  {row.pillar && !row.moduleName.includes(`— ${row.pillar}`) ? (
                    <div className="text-xs text-secondary-500">{row.pillar}</div>
                  ) : null}
                </td>
                <td>
                  <PermissionEffectToggle
                    value={row.accessEffect}
                    disabled={!canWrite}
                    onChange={(value) => updateRow(row.moduleCode, { accessEffect: value })}
                  />
                </td>
                <td>
                  <PermissionEffectToggle
                    value={row.readEffect}
                    disabled={!canWrite}
                    onChange={(value) => updateRow(row.moduleCode, { readEffect: value })}
                  />
                </td>
                <td>
                  <PermissionEffectToggle
                    value={row.writeEffect}
                    disabled={!canWrite}
                    onChange={(value) => updateRow(row.moduleCode, { writeEffect: value })}
                  />
                </td>
                <td>
                  <PermissionEffectToggle
                    value={row.deleteEffect}
                    disabled={!canWrite}
                    onChange={(value) => updateRow(row.moduleCode, { deleteEffect: value })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}
